/**
 * Brand hero — the marble-and-gold visual that anchors auth pages (/login, /signup).
 *
 * Pure CSS + tiny inline SVG (the sparkle divider) so the component ships at virtually no
 * bundle cost and stays sharp at any zoom level. Composition:
 *
 *   • "LINKORA" wordmark in antique-gold gradient (clamp-scaled for phone → 5K).
 *   • Hairline divider with a four-pointed sparkle as the centerpiece.
 *   • Tagline "Link Beyond · Connected Without Limits" in muted gold.
 *   • Optional subtitle (page-specific status line — e.g. "Sign in to your secure session").
 *
 * The marble texture itself lives in `styles.css` under `.brand-stage` — keeping background
 * concerns in CSS lets the same hero reuse on any page that wants the brand stage (auth, and
 * future signup / first-time setup flows).
 */
export function BrandHero({ subtitle }: { subtitle?: string }) {
  return (
    <section className="brand-hero">
      <h1 className="brand-hero-wordmark">LINKORA</h1>
      <div className="brand-hero-divider" aria-hidden="true">
        <span className="brand-hero-divider-line" />
        <SparkleIcon />
        <span className="brand-hero-divider-line" />
      </div>
      <p className="brand-hero-tagline">Link Beyond · Connected Without Limits</p>
      {subtitle && <p className="brand-hero-subtitle">{subtitle}</p>}
    </section>
  );
}

/** Four-pointed sparkle used as the centerpiece of the tagline divider. Inherits the
 *  surrounding gold colour via `currentColor` so we don't redeclare a gradient. */
function SparkleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="brand-hero-sparkle"
    >
      <path
        d="M 12 1 L 14 10 L 23 12 L 14 14 L 12 23 L 10 14 L 1 12 L 10 10 Z"
        fill="currentColor"
      />
    </svg>
  );
}
